import ApiKeyDisplayModal from '@/components/ApiKeyDisplayModal';
import ApiKeyModal from '@/components/ApiKeyModal';
import ConfirmDialog from '@/components/ConfirmDialog';
import WebhookModal from '@/components/WebhookModal';
import { useApiKeys } from '@/hooks/useApiKeys';
import { useToast } from '@/hooks/useToast';
import { useWebhooks } from '@/hooks/useWebhooks';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Utility function to safely extract API key ID
const getApiKeyId = apiKey => {
  const id = apiKey?.id || apiKey?._id || apiKey?.apiKeyId;
  if (!id) {
    console.warn('API key object missing ID field:', apiKey);
    console.warn('Available fields:', Object.keys(apiKey || {}));
  }
  return id;
};

/**
 * Developer Hub page component for managing API keys and webhooks
 * @returns {JSX.Element} DeveloperHub component
 */
const DeveloperHub = () => {
  const { projectId } = useParams();
  const { showSuccess, showError, showLoading, dismissToast } = useToast();

  // Custom hooks for API keys and webhooks
  const {
    apiKeys,
    loading: apiKeysLoading,
    error: apiKeysError,
    fetchApiKeys,
    createApiKey,
    updateApiKey,
    deleteApiKey,
    regenerateApiKey,
    clearError: clearApiKeysError,
  } = useApiKeys(projectId);

  const {
    webhooks,
    loading: webhooksLoading,
    error: webhooksError,
    fetchWebhooks,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    testWebhook,
    getAvailableEvents,
    clearError: clearWebhooksError,
  } = useWebhooks(projectId);

  // State management
  const [activeTab, setActiveTab] = useState('api-keys');
  const [showApiKeyForm, setShowApiKeyForm] = useState(false);
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState(null);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [showApiKeyDisplayModal, setShowApiKeyDisplayModal] = useState(false);
  const [newlyCreatedApiKey, setNewlyCreatedApiKey] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    isOpen: false,
    type: null, // 'apiKey' or 'webhook'
    item: null,
  });

  // Track if data has been loaded for each tab
  const [dataLoaded, setDataLoaded] = useState({
    'api-keys': false,
    webhooks: false,
  });

  // Get webhook events from service
  const webhookEvents = getAvailableEvents();

  // Handle tab changes and load data on first visit
  const handleTabChange = useCallback(
    tabName => {
      setActiveTab(tabName);

      // Load data if not already loaded
      if (!dataLoaded[tabName]) {
        if (tabName === 'api-keys') {
          fetchApiKeys();
        } else if (tabName === 'webhooks') {
          fetchWebhooks();
        }
        setDataLoaded(prev => ({ ...prev, [tabName]: true }));
      }
    },
    [dataLoaded, fetchApiKeys, fetchWebhooks]
  );

  // Load initial tab data when component mounts
  useEffect(() => {
    if (projectId && !dataLoaded['api-keys']) {
      handleTabChange('api-keys');
    }
  }, [projectId, dataLoaded, handleTabChange]);

  // Handle errors from hooks
  useEffect(() => {
    if (apiKeysError) {
      showError(apiKeysError);
      clearApiKeysError();
    }
  }, [apiKeysError, showError, clearApiKeysError]);

  useEffect(() => {
    if (webhooksError) {
      showError(webhooksError);
      clearWebhooksError();
    }
  }, [webhooksError, showError, clearWebhooksError]);

  // API Key handlers
  const onApiKeySubmit = async data => {
    try {
      const loadingToast = showLoading(
        editingApiKey ? 'Updating API key...' : 'Creating API key...'
      );

      let response;
      if (editingApiKey) {
        // Get the API key ID from the editingApiKey object
        const apiKeyId = getApiKeyId(editingApiKey);
        if (!apiKeyId) {
          console.error('Editing API key missing ID:', editingApiKey);
          showError(
            'Invalid API key data. Please refresh the page and try again.'
          );
          return;
        }

        response = await updateApiKey(apiKeyId, data.name);
      } else {
        response = await createApiKey(data.name);
      }

      setShowApiKeyForm(false);
      setEditingApiKey(null);
      dismissToast(loadingToast);

      if (editingApiKey) {
        showSuccess('API key updated successfully');
      } else {
        // Show the display modal for newly created API keys
        if (response && response.key) {
          setNewlyCreatedApiKey(response);
          setShowApiKeyDisplayModal(true);
        } else {
          showSuccess('API key created successfully');
        }
      }
    } catch (error) {
      console.error('Error saving API key:', error);
      showError('Failed to save API key');
    }
  };

  const handleEditApiKey = apiKey => {
    // Check if apiKey has a valid ID
    const apiKeyId = getApiKeyId(apiKey);
    if (!apiKeyId) {
      console.error('API key object missing ID:', apiKey);
      showError('Invalid API key data. Please refresh the page and try again.');
      return;
    }

    setEditingApiKey(apiKey);
    setShowApiKeyForm(true);
  };

  const handleDeleteApiKey = async apiKeyId => {
    try {
      const loadingToast = showLoading('Deleting API key...');

      await deleteApiKey(apiKeyId);

      dismissToast(loadingToast);
      showSuccess('API key deleted successfully');
    } catch (error) {
      console.error('Error deleting API key:', error);
      showError('Failed to delete API key');
    }
  };

  const confirmDeleteApiKey = apiKey => {
    // Check if apiKey has a valid ID
    const apiKeyId = getApiKeyId(apiKey);
    if (!apiKeyId) {
      console.error('API key object missing ID:', apiKey);
      showError('Invalid API key data. Please refresh the page and try again.');
      return;
    }

    setDeleteConfirmDialog({
      isOpen: true,
      type: 'apiKey',
      item: apiKey,
    });
  };

  const confirmDeleteWebhook = webhook => {
    setDeleteConfirmDialog({
      isOpen: true,
      type: 'webhook',
      item: webhook,
    });
  };

  const handleConfirmDelete = async () => {
    const { type, item } = deleteConfirmDialog;

    // Capture the values before resetting state
    const itemType = type;
    const itemId =
      itemType === 'apiKey'
        ? getApiKeyId(item)
        : item?.id || item?._id || item?.webhookId;

    // Reset the dialog state immediately
    setDeleteConfirmDialog({ isOpen: false, type: null, item: null });

    try {
      if (itemType === 'apiKey' && itemId) {
        await handleDeleteApiKey(itemId);
      } else if (itemType === 'webhook' && itemId) {
        await handleDeleteWebhook(itemId);
      } else {
        console.error('Invalid delete request:', { itemType, itemId });
        showError('Invalid item data. Please refresh the page and try again.');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      showError('Failed to delete item');
    }
  };

  const handleRegenerateApiKey = async apiKeyId => {
    if (!apiKeyId) {
      console.error('API key ID is undefined or null');
      showError('Invalid API key ID. Please refresh the page and try again.');
      return;
    }

    try {
      const loadingToast = showLoading('Regenerating API key...');

      const response = await regenerateApiKey(apiKeyId);

      dismissToast(loadingToast);

      // Show the new full key in display modal
      if (response && response.key) {
        setNewlyCreatedApiKey(response);
        setShowApiKeyDisplayModal(true);
      } else {
        showSuccess('API key regenerated successfully');
      }
    } catch (error) {
      console.error('Error regenerating API key:', error);
      showError('Failed to regenerate API key');
    }
  };

  // Webhook handlers
  const onWebhookSubmit = async data => {
    try {
      const loadingToast = showLoading(
        editingWebhook ? 'Updating webhook...' : 'Creating webhook...'
      );

      if (editingWebhook) {
        await updateWebhook(
          editingWebhook.id,
          data.name,
          data.url,
          data.events,
          data.isActive
        );
      } else {
        await createWebhook(data.name, data.url, data.events, data.isActive);
      }

      setShowWebhookForm(false);
      setEditingWebhook(null);
      dismissToast(loadingToast);
      showSuccess(
        editingWebhook
          ? 'Webhook updated successfully'
          : 'Webhook created successfully'
      );
    } catch (error) {
      console.error('Error saving webhook:', error);
      showError('Failed to save webhook');
    }
  };

  const handleEditWebhook = webhook => {
    setEditingWebhook(webhook);
    setShowWebhookForm(true);
  };

  const handleDeleteWebhook = async webhookId => {
    try {
      const loadingToast = showLoading('Deleting webhook...');

      await deleteWebhook(webhookId);

      dismissToast(loadingToast);
      showSuccess('Webhook deleted successfully');
    } catch (error) {
      console.error('Error deleting webhook:', error);
      showError('Failed to delete webhook');
    }
  };

  const handleTestWebhook = async webhookId => {
    try {
      const loadingToast = showLoading('Testing webhook...');

      const result = await testWebhook(webhookId);

      dismissToast(loadingToast);

      if (result.status >= 200 && result.status < 300) {
        showSuccess(
          `Webhook test successful! Status: ${result.status}, Duration: ${result.duration}ms`
        );
      } else {
        showError(
          `Webhook test failed! Status: ${result.status}, Response: ${result.response}`
        );
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      showError('Failed to test webhook');
    }
  };

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text);
    showSuccess('Copied to clipboard');
  };

  const isLoading = apiKeysLoading || webhooksLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75">
          <div className="flex items-center bg-white rounded-lg p-6">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="text-gray-700">Loading developer settings...</span>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Developer Hub
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage API keys and webhook configurations for this project
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 sm:px-0">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => handleTabChange('api-keys')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'api-keys'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                API Keys
              </button>
              <button
                onClick={() => handleTabChange('webhooks')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'webhooks'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Webhooks
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-0 mt-6">
          {activeTab === 'api-keys' ? (
            <div className="space-y-6">
              {/* API Keys Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">API Keys</h2>
                <button
                  onClick={() => {
                    setEditingApiKey(null);
                    setShowApiKeyForm(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg
                    className="-ml-1 mr-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Create API Key
                </button>
              </div>

              {/* API Keys List */}
              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                {apiKeys.length === 0 ? (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No API keys
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Get started by creating your first API key.
                    </p>
                    <div className="mt-6">
                      <button
                        onClick={() => {
                          setEditingApiKey(null);
                          setShowApiKeyForm(true);
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <svg
                          className="-ml-1 mr-2 h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Create API Key
                      </button>
                    </div>
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {apiKeys.map(apiKey => (
                      <li key={apiKey.id}>
                        <div className="px-4 py-4 sm:px-6">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-primary-600 truncate">
                                  {apiKey.name}
                                </p>
                                <div className="ml-2 flex-shrink-0 flex">
                                  <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                    Active
                                  </p>
                                </div>
                              </div>
                              <div className="mt-2 flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                  <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                                    {apiKey.key}
                                  </code>
                                </div>
                                <p className="text-xs text-gray-500">
                                  Created:{' '}
                                  {new Date(
                                    apiKey.createdAt
                                  ).toLocaleDateString()}
                                </p>
                                {apiKey.lastUsed && (
                                  <p className="text-xs text-gray-500">
                                    Last used:{' '}
                                    {new Date(
                                      apiKey.lastUsed
                                    ).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="ml-4 flex-shrink-0 flex space-x-2">
                              <button
                                onClick={() => handleEditApiKey(apiKey)}
                                className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() =>
                                  handleRegenerateApiKey(getApiKeyId(apiKey))
                                }
                                className="text-yellow-600 hover:text-yellow-900 text-sm font-medium"
                              >
                                Regenerate
                              </button>
                              <button
                                onClick={() => confirmDeleteApiKey(apiKey)}
                                className="text-red-600 hover:text-red-900 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Webhooks Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Webhooks</h2>
                <button
                  onClick={() => {
                    setEditingWebhook(null);
                    setShowWebhookForm(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg
                    className="-ml-1 mr-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Create Webhook
                </button>
              </div>

              {/* Webhooks List */}
              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                {webhooks.length === 0 ? (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No webhooks
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Get started by creating your first webhook.
                    </p>
                    <div className="mt-6">
                      <button
                        onClick={() => {
                          setEditingWebhook(null);
                          setShowWebhookForm(true);
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <svg
                          className="-ml-1 mr-2 h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Create Webhook
                      </button>
                    </div>
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {webhooks.map(webhook => (
                      <li key={webhook.id}>
                        <div className="px-4 py-4 sm:px-6">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-primary-600 truncate">
                                  {webhook.name}
                                </p>
                                <div className="ml-2 flex-shrink-0 flex">
                                  <p
                                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      webhook.isActive
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    {webhook.isActive ? 'Active' : 'Inactive'}
                                  </p>
                                </div>
                              </div>
                              <p className="mt-1 text-sm text-gray-500 break-all">
                                {webhook.url}
                              </p>
                              <div className="mt-2 flex items-center space-x-4">
                                <div className="flex flex-wrap gap-1">
                                  {webhook.events.map(event => (
                                    <span
                                      key={event}
                                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                      {webhookEvents.find(e => e.id === event)
                                        ?.label || event}
                                    </span>
                                  ))}
                                </div>
                                <p className="text-xs text-gray-500">
                                  Created:{' '}
                                  {new Date(
                                    webhook.createdAt
                                  ).toLocaleDateString()}
                                </p>
                                {webhook.lastTriggered && (
                                  <p className="text-xs text-gray-500">
                                    Last triggered:{' '}
                                    {new Date(
                                      webhook.lastTriggered
                                    ).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="ml-4 flex-shrink-0 flex space-x-2">
                              <button
                                onClick={() => handleEditWebhook(webhook)}
                                className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleTestWebhook(webhook.id)}
                                className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                              >
                                Test
                              </button>
                              <button
                                onClick={() => confirmDeleteWebhook(webhook)}
                                className="text-red-600 hover:text-red-900 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* API Key Modal */}
      <ApiKeyModal
        isOpen={showApiKeyForm}
        apiKey={editingApiKey}
        onSubmit={onApiKeySubmit}
        onCancel={() => {
          setShowApiKeyForm(false);
          setEditingApiKey(null);
        }}
        isLoading={apiKeysLoading}
      />

      {/* Webhook Modal */}
      <WebhookModal
        isOpen={showWebhookForm}
        webhook={editingWebhook}
        onSubmit={onWebhookSubmit}
        onCancel={() => {
          setShowWebhookForm(false);
          setEditingWebhook(null);
        }}
        isLoading={webhooksLoading}
      />

      {/* API Key Display Modal */}
      <ApiKeyDisplayModal
        isOpen={showApiKeyDisplayModal}
        apiKey={newlyCreatedApiKey}
        onClose={() => {
          setShowApiKeyDisplayModal(false);
          setNewlyCreatedApiKey(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen}
        title={`Delete ${deleteConfirmDialog.type === 'apiKey' ? 'API Key' : 'Webhook'}`}
        message={`Are you sure you want to delete "${deleteConfirmDialog.item?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() =>
          setDeleteConfirmDialog({ isOpen: false, type: null, item: null })
        }
        isLoading={apiKeysLoading || webhooksLoading}
      />
    </div>
  );
};

export default DeveloperHub;
